/**
 * Второй фактор: привязка телефона к ключу владельца.
 *
 * Ключ доказывает владение устройством — этого довольно, чтобы посмотреть, и
 * мало, чтобы стереть. Здесь владелец добавляет к ключу то, что доказывает
 * присутствие человека.
 *
 * Порядок шагов не украшение. Пока код не подтверждён, фактор НЕ работает:
 * иначе неудачная привязка — сфотографировали QR, а приложение не сохранило —
 * заперла бы владельца снаружи от собственных опасных операций.
 */
import { useState } from 'react';
import { useQuery } from '../transport/hooks.ts';
import { request } from '../transport/socket.ts';

interface Status {
  bound: boolean;
  active: boolean;
  confirmedAt: string | null;
  recoveryLeft: number;
}

export function TwoFactor(): React.JSX.Element {
  const { data, reload } = useQuery<Status>('totp.status');
  const [started, setStarted] = useState<{ secret: string; uri: string; svg: string } | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const say = (err: unknown): void =>
    setFailure((err as { msg?: string }).msg ?? 'не получилось');

  async function begin(): Promise<void> {
    setBusy(true); setFailure(null);
    try { setStarted(await request('totp.begin')); } catch (err) { say(err); }
    finally { setBusy(false); }
  }

  async function confirm(): Promise<void> {
    setBusy(true); setFailure(null);
    try {
      const r = await request<{ recoveryCodes: string[] }>('totp.confirm', { code: code.trim() });
      setCodes(r.recoveryCodes);
      setStarted(null);
      setCode('');
      reload();
    } catch (err) { say(err); }
    finally { setBusy(false); }
  }

  async function disable(): Promise<void> {
    setBusy(true); setFailure(null);
    try {
      await request('totp.disable', { code: code.trim() });
      setCode('');
      reload();
    } catch (err) { say(err); }
    finally { setBusy(false); }
  }

  return (
    <section className="twofa">
      <h3 className="twofa__title">Второй фактор</h3>

      {data?.active ? (
        <>
          <p className="twofa__state twofa__state--on">
            Работает с {new Date(data.confirmedAt!).toLocaleDateString('ru-RU')}.
            Опасные действия просят код — и на этом экране, и у помощника.
          </p>
          <p className="muted">
            Запасных кодов осталось: {data.recoveryLeft}
            {data.recoveryLeft <= 2 && ' — стоит перевыпустить'}
          </p>
          <div className="twofa__row">
            <input
              className="input otp__input mono" value={code} placeholder="код"
              onChange={(e) => setCode(e.target.value)} maxLength={32}
            />
            <button type="button" className="button button--ghost button--danger"
                    disabled={busy || code.trim().length < 4} onClick={() => void disable()}>
              Убрать фактор
            </button>
          </div>
          <p className="muted twofa__note">
            Снятие тоже требует кода: иначе это был бы способ обойти защиту.
          </p>
        </>
      ) : started ? (
        <>
          <p className="twofa__state">
            Наведите приложение-аутентификатор на код. Не читается с экрана —
            введите ключ вручную.
          </p>
          <div className="twofa__qr" dangerouslySetInnerHTML={{ __html: started.svg }} />
          <p className="twofa__secret mono">{started.secret}</p>
          <div className="twofa__row">
            <input
              className="input otp__input mono" value={code} placeholder="000000"
              inputMode="numeric" autoComplete="one-time-code"
              onChange={(e) => setCode(e.target.value)} maxLength={32}
            />
            <button type="button" className="button" disabled={busy || code.trim().length < 6}
                    onClick={() => void confirm()}>
              Подтвердить
            </button>
            <button type="button" className="button button--ghost"
                    onClick={() => { setStarted(null); setCode(''); }}>
              Отмена
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="twofa__state twofa__state--off">
            Не привязан. Опасные действия сейчас подтверждения не требуют.
          </p>
          <button type="button" className="button" disabled={busy} onClick={() => void begin()}>
            Привязать телефон
          </button>
        </>
      )}

      {codes && (
        <div className="twofa__codes">
          <h4>Запасные коды</h4>
          <p>
            Показываются <b>один раз</b>: у консоли остаются только их хеши.
            Распечатайте или сохраните вне телефона — они понадобятся ровно
            тогда, когда телефона не будет под рукой. Каждый работает однажды.
          </p>
          <ul className="twofa__codes-list mono">
            {codes.map((c) => <li key={c}>{c}</li>)}
          </ul>
          <button type="button" className="button button--ghost" onClick={() => setCodes(null)}>
            Сохранил
          </button>
        </div>
      )}

      {failure && <p className="alert alert--error">{failure}</p>}
    </section>
  );
}
